export default `<p><strong>Dokumentversion:</strong> V3.28 &nbsp;|&nbsp; <strong>Veröffentlichungsdatum:</strong> 2022 &nbsp;|&nbsp; <strong>Dokumenttyp:</strong> Produkt-Whitepaper &amp; Sicherheitsbewertung</p>

<h2>1. Zusammenfassung</h2>
<p>Dieses Lending-Pool-Modul ist ein On-Chain-Liquiditätsdienst für Kreditvergabe und -aufnahme, der auf DeFi-Geldmarktprotokollen basiert. Über Open-Source-Smart-Contracts und programmierbare Zinsmodelle verbindet das System digitale Vermögenswerte der Nutzer mit etablierten On-Chain-Kreditmärkten und ermöglicht <strong>Verzinsung von Einlagen, besicherte Kredite und geteilte Liquidität</strong>.</p>
<p>Das Modul erfordert keine manuelle Intervention bei der täglichen Mittelallokation. Zinssätze, Kapazitätsgrenzen und Auslastung werden in Echtzeit durch On-Chain-Märkte bestimmt und durch Multi-Source-Datenaggregation transparent dargestellt.</p>

<h2>2. Branchenhintergrund &amp; Produktpositionierung</h2>
<p>Traditionelle Kreditvergabe hängt von zentralisierten Institutionen ab. DeFi-Kreditprotokolle nutzen Smart Contracts zur automatischen Matching von Angebot und Nachfrage mit Überbesicherung und Liquidationsmechanismen.</p>
<p>Positionierung dieses Lending-Pool-Moduls:</p>
<ul>
  <li><strong>On-Chain-Geldmarkt-Gateway:</strong> Aggregiert Lending-Pool-Daten über mehrere Chains und Protokolle;</li>
  <li><strong>Programmatische Ertragsverteilung:</strong> Automatische Abrechnung nach festem Zeitplan basierend auf autorisierten Salden und Markt-APY;</li>
  <li><strong>Transparent &amp; überprüfbar:</strong> TVL, APY, Auslastung und LTV sind auf öffentliche On-Chain-Daten zurückführbar;</li>
  <li><strong>Non-Custodial:</strong> Autorisierung und On-Chain-Interaktionen werden vom Wallet des Nutzers initiiert.</li>
</ul>

<h2>3. Systemarchitektur</h2>
<h3>3.1 Architekturübersicht</h3>
<ul>
  <li><strong>Benutzeroberfläche:</strong> Wallet-Verbindung, Autorisierung, Pool-Browsing, Ertragsabfragen;</li>
  <li><strong>Geschäftsservice-Schicht:</strong> Authentifizierung, Autorisierungsverwaltung, Ertragsaufträge, mehrsprachige Inhalte;</li>
  <li><strong>Datenaggregationsschicht:</strong> Synchronisiert Metriken aus öffentlichen DeFi-Datenquellen;</li>
  <li><strong>On-Chain-Protokollschicht:</strong> Geprüfte Open-Source-Verträge führen Ein- und Auszahlungslogik aus.</li>
</ul>

<h3>3.2 Kernmetriken</h3>
<table>
  <thead>
    <tr><th>Metrik</th><th>Beschreibung</th></tr>
  </thead>
  <tbody>
    <tr><td>TVL</td><td>Gesamtvermögen im Pool in USD</td></tr>
    <tr><td>Supply APY</td><td>Jährliche Einlagenrendite aus Basiszins und Protokollbelohnungen</td></tr>
    <tr><td>Borrow APY</td><td>Jährliche Kosten für Kreditnehmer</td></tr>
    <tr><td>Auslastung</td><td>Verhältnis geliehener zu verfügbaren Mitteln</td></tr>
    <tr><td>Max LTV</td><td>Maximales Loan-to-Value-Verhältnis vor Liquidation</td></tr>
    <tr><td>Supply / Borrow Cap</td><td>Asset-spezifische Limits zur Risikokontrolle</td></tr>
    <tr><td>Verfügbare Liquidität</td><td>Sofort verfügbare Kreditliquidität</td></tr>
  </tbody>
</table>

<h2>4. Nutzerablauf &amp; Ertragsmechanik</h2>
<ol>
  <li><strong>Wallet verbinden</strong> über Web3-Wallet;</li>
  <li><strong>Vermögenswerte autorisieren</strong> (USDT, USDC usw.) per On-Chain Approve;</li>
  <li><strong>Lending Pool beitreten</strong> zur Aufnahme in die Ertragsberechnungswarteschlange;</li>
  <li><strong>Ertragsabrechnung</strong> täglich basierend auf autorisiertem Saldo und Marktzinsen;</li>
  <li><strong>Vermögenswerte einsehen</strong> unter „Mein Portfolio“.</li>
</ol>
<p><strong>Historische APY garantiert keine zukünftigen Erträge.</strong></p>

<h3>4.3 Gebühren</h3>
<h4>Wie werden DeFi-Earn-Gebühren erhoben?</h4>
<p>Für On-Chain-Transaktionen fallen Gas-Gebühren an, um Smart-Contract-Interaktionen wie Zeichnung und Ertragsabholung abzuschließen. Bei bestimmten Vermögenswerten können der DeFi-Lending-Pool und Vault-Manager eine Performance-Gebühr als Prozentsatz Ihres tatsächlichen Gewinns erheben; Ihr Kapital bleibt unberührt.</p>

<h2>5. Sicherheitsdesign</h2>
<ul>
  <li>Basisprotokolle nutzen geprüfte Open-Source-Verträge;</li>
  <li>Non-Custodial-Architektur; keine Speicherung privater Schlüssel;</li>
  <li>Minimale Token-Approve-Berechtigungen; jederzeit im Wallet widerrufbar;</li>
  <li>Token-Auth, HTTPS, RBAC-Administration und Audit-Trails.</li>
</ul>

<h2>6. Sicherheitsbewertung</h2>
<table>
  <thead>
    <tr><th>Punkt</th><th>Ergebnis</th><th>Hinweise</th></tr>
  </thead>
  <tbody>
    <tr><td>Smart-Contract-Abhängigkeiten</td><td><strong>Bestanden</strong></td><td>Öffentlich geprüfte DeFi-Protokolle</td></tr>
    <tr><td>Non-Custodial &amp; Schlüsselverwaltung</td><td><strong>Bestanden</strong></td><td>Keine Speicherung privater Schlüssel</td></tr>
    <tr><td>API &amp; Authentifizierung</td><td><strong>Bestanden</strong></td><td>Token-Auth und HTTPS implementiert</td></tr>
    <tr><td>Datensynchronisation</td><td><strong>Bestanden</strong></td><td>Geplante Syncs mit Snapshots</td></tr>
    <tr><td>Ertragsabrechnungslogik</td><td><strong>Bestanden</strong></td><td>Klare Regeln; prüfbare Aufträge</td></tr>
  </tbody>
</table>

<h2>7. Risikohinweise</h2>
<ul>
  <li><strong>Smart-Contract-Risiko:</strong> Unentdeckte Schwachstellen möglich;</li>
  <li><strong>Marktrisiko:</strong> APY kann stark schwanken;</li>
  <li><strong>Liquidationsrisiko:</strong> Kollateralpreisverfall kann Liquidation auslösen;</li>
  <li><strong>Stablecoin-De-Peg-Risiko:</strong> USDT/USDC kann von $1,00 abweichen;</li>
  <li><strong>Regulierungsrisiko:</strong> DeFi-Regulierung entwickelt sich weltweit.</li>
</ul>
<p><strong>Dieses Modul bietet keine Anlageberatung und garantiert keine festen Erträge.</strong></p>

<h2>8. Glossar</h2>
<ul>
  <li><strong>APY:</strong> Annual Percentage Yield;</li>
  <li><strong>TVL:</strong> Total Value Locked;</li>
  <li><strong>LTV:</strong> Loan-to-Value-Verhältnis;</li>
  <li><strong>Approve:</strong> ERC-20-Token-Autorisierung;</li>
  <li><strong>Liquidation:</strong> Automatischer Verkauf von Sicherheiten bei Unterbesicherung.</li>
</ul>

<p>— Nur zu Informationszwecken. Keine Rechts-, Finanz- oder Anlageberatung. On-Chain-Daten über Block-Explorer und offizielle Protokoll-Schnittstellen verifizieren.</p>`
